/// <amd-module name="Build/Admin/Pool/Components/PoolAgentsContainer" />
import * as React from "react";

import { FormatComponent } from "VSS/Platform/Components/Format";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";

import { Card } from "VSSUI/Card";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

import { TaskAgent, TaskAgentPool, TaskAgentQueue, TaskAgentPoolOptions } from "DistributedTask/Client/Contracts/TaskAgent";

import { ConfirmButtonType } from "Build/Admin/Common/Actions/ConfirmationDialogActions";
import { Assets } from "Build/Admin/Common/Constants";
import { IPoolAdminLinkingService } from "Build/Admin/Common/Services/PoolAdminLinkingService";
import { isElastic } from "Build/Admin/Common/TaskAgentPoolUtils";
import { PoolActionCreator } from "Build/Admin/Pool/Actions/PoolActionCreator";
import { AgentList } from "Build/Admin/Pool/Components/AgentList";
import { StoresHub } from "Build/Admin/Pool/Stores/StoresHub";

import * as Resources from "Build/Admin/Pool/Resources";

export interface IPoolAgentsContainerProps {
    storesHub: StoresHub;
    actionCreator: PoolActionCreator;
}

export interface IPoolAgentsContainerState {
    agents: TaskAgent[];
    selectedAgentQueue: TaskAgentQueue | undefined;
    selectedAgentPool: TaskAgentPool | undefined;
}

export class PoolAgentsContainer extends VssComponent<IPoolAgentsContainerProps, IPoolAgentsContainerState> {
    constructor(props: IPoolAgentsContainerProps, context: IVssComponentContext) {
        super(props, context);
        this.state = this._getStateFromStores();
    }

    public render(): JSX.Element {
        const primaryText: string = isElastic(this.state.selectedAgentPool) ? Resources.ZeroElasticAgentsPrimaryText : Resources.ZeroAgentsPrimaryText;
        const secondaryText: string = isElastic(this.state.selectedAgentPool) ? Resources.ZeroElasticAgentsSecondaryText : Resources.ZeroAgentsSecondaryText;
        const showNewAgentButton: boolean = !isElastic(this.state.selectedAgentPool);
        return (
            <div className="pipelines-pool-agents page-content page-content-top">
            {
                this._shouldRenderZeroData() &&
                <ZeroData
                    primaryText={primaryText}
                    secondaryText={secondaryText}
                    actionType={showNewAgentButton ? ZeroDataActionType.ctaButton : undefined}
                    actionText={showNewAgentButton ? Resources.NewAgent : undefined}
                    onActionClick={showNewAgentButton ? () => this.props.actionCreator.showDownloadAgentDialog() : undefined}
                    imagePath={getAssetLocation(this.context.pageContext, Assets.ZeroDataPng)}
                    imageAltText={primaryText} />
            }
            {
                !this._shouldRenderZeroData() &&
                <Card className="flex-grow bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                    <AgentList
                        agents={this.state.agents}
                        agentLinkProvider={this._getLinkToAgent}
                        onAgentEnabledChanged={this._onAgentEnabledChanged}
                        onUpdateAgent={this._onUpdateAgent}
                        onDeleteAgent={this._onDeleteAgent}
                        pageContext={this.context.pageContext}
                        selectedAgentPool={this.state.selectedAgentPool}
                    />
                </Card>
            }
            </div>
        );
    }

    public componentDidMount(): void {
        this.props.storesHub.agentStore.addChangedListener(this._onChanged);
        this.props.storesHub.selectionStore.addChangedListener(this._onChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.agentStore.removeChangedListener(this._onChanged);
        this.props.storesHub.selectionStore.removeChangedListener(this._onChanged);
    }

    private _onChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores(): IPoolAgentsContainerState {
        return {
            agents: this.props.storesHub.agentStore.state,
            selectedAgentQueue: this.props.storesHub.selectionStore.state.selectedAgentQueue,
            selectedAgentPool: this.props.storesHub.selectionStore.state.selectedAgentPool
        };
    }

    private _shouldRenderZeroData(): boolean {
        return this.state.agents.length === 0;
    }

    private _getLinkToAgent = (agent: TaskAgent): string | undefined => {
        if (this.state.selectedAgentQueue || this.state.selectedAgentPool) {
            const linkingService: IPoolAdminLinkingService = this.context.pageContext.getService<IPoolAdminLinkingService>("IPoolAdminLinkingService");
            return linkingService.getAgentUrl(this.state.selectedAgentQueue, this.state.selectedAgentPool, agent);
        }
        else {
            return undefined;
        }
    }

    private _onAgentEnabledChanged = (agent: TaskAgent, checked: boolean): void => {
        if (this.state.selectedAgentPool) {
            this.props.actionCreator.enableAgent(agent, this.state.selectedAgentPool.id, checked);
        }
    }

    private _onUpdateAgent = (agent: TaskAgent): void => {
        const content: JSX.Element = (
            <FormatComponent format={Resources.ConfirmUpdateAgentMessage} >
                <span className="fontWeightSemiBold">{agent.name}</span>
                <br />
                <br />
            </FormatComponent>
        );
        this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
            agent,
            Resources.ConfirmUpdateTitle,
            content,
            ConfirmButtonType.Update,
            this._confirmUpdateAgent,
            this._cancelConfirmationDialog);
    }

    private _confirmUpdateAgent = (agent: TaskAgent): void => {
        if (this.state.selectedAgentPool) {
            this.props.actionCreator.pushAgentUpdateRequest(this.state.selectedAgentPool.id, agent);
            this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
        }
    }

    private _onDeleteAgent = (agent: TaskAgent): void => {
        let queueOrPoolName: string | undefined;

        if (this.state.selectedAgentQueue) {
            queueOrPoolName = this.state.selectedAgentQueue.name;
        }
        else if (this.state.selectedAgentPool) {
            queueOrPoolName = this.state.selectedAgentPool.name;
        }

        if (queueOrPoolName) {
            const content: JSX.Element = (
                <FormatComponent format={Resources.ConfirmDeleteAgentMessage} >
                    <span className="fontWeightSemiBold">{agent.name}</span>
                    <br />
                    <br />
                    <span className="fontWeightSemiBold">{queueOrPoolName}</span>
                </FormatComponent>
            );

            this.props.actionCreator.confirmationDialogActionCreator.showConfirmationDialog(
                agent,
                Resources.ConfirmDeleteTitle,
                content,
                ConfirmButtonType.Delete,
                this._confirmDeleteAgent,
                this._cancelConfirmationDialog);
        }
    }

    private _confirmDeleteAgent = (agent: TaskAgent): void => {
        if (this.state.selectedAgentPool) {
            this.props.actionCreator.deleteAgent(agent, this.state.selectedAgentPool.id);
            this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
        }
    }

    private _cancelConfirmationDialog = (): void => {
        this.props.actionCreator.confirmationDialogActionCreator.dismissConfirmationDialog();
    }
}
